/**
 * Rainbow Mulch — Playground Project Builder
 * SETTLED BUSINESS CONSTANTS
 *
 * This file is the single source of truth for business rules in the front end,
 * per D-010: "Business rules live in configuration, never in templates."
 *
 * Every value here is SETTLED and traceable to a decision or business rule.
 * Nothing in this file is a guess. Nothing in this file is a mock.
 * Mocked/derived values live in pb-mocks.js and are replaced in Sprint 4.
 *
 * If a value here changes, it changes HERE and nowhere else. Do not copy any of
 * these numbers into a .liquid template or into pb-builder.js.
 *
 * FUTURE: Sprint 8 may move these into Shopify metafields so they are editable
 * without a deploy. The shape below is designed to survive that move.
 */

window.PBConfig = Object.freeze({
  /* ---------------------------------------------------------------------
   * PACKAGING & PRICING — BR-02, BR-10..BR-14, D-021
   * Pricing varies by packaging ONLY. Never by colour (BR-01, D-008).
   * ------------------------------------------------------------------- */
  /**
   * D-032 — material density, a property of the MULCH and not of the packaging.
   *
   * Confirmed 16.0 lbs per cubic foot. The two independently stated weights in
   * BR-02 corroborate it exactly:
   *   bags:  16 lbs per 1 cu ft bag                        -> 16.00 lbs/cu ft
   *   totes: (650 - 10 tare) / 40 cu ft                    -> 16.00 lbs/cu ft
   * So a bag's stated 16 lbs is material with negligible bag tare, and the
   * tote's extra 0.25 lbs/cu ft was always its 10 lb container.
   */
  material: Object.freeze({
    lbsPerCubicFoot: 16 // BR-02, BR-14, D-032
  }),

  packaging: Object.freeze({
    bags: Object.freeze({
      id: 'bags',
      label: 'Cubic Foot Bags',
      pricePerCubicFoot: 61.75, // BR-02
      // D-031 — CONFIRMED exactly 1 cu ft. Bags are portioned BY WEIGHT during
      // filling to hit that volume precisely, so this is a manufacturing
      // guarantee, not a nominal label. bagCount === billedCubicFeet always.
      cubicFeetPerUnit: 1, // BR-10, D-031
      // Weight of one FULL unit. Reference and display only — NOT used in the
      // weight calculation, which is material density + tare (BR-14, D-032).
      fullUnitWeightLbs: 16, // BR-02
      tareLbsPerUnit: 0, // a bag's own weight is negligible (D-032)
      unitNoun: 'bag',
      unitNounPlural: 'bags'
    }),
    tote: Object.freeze({
      id: 'tote',
      label: 'Bulk Tote',
      pricePerCubicFoot: 58.9, // BR-02
      cubicFeetPerUnit: 40, // BR-11 — tote capacity
      // Weight of one FULL tote (40 cu ft of material + container).
      // Reference and display only — NOT used in the weight calculation.
      // A partially-filled tote weighs LESS than this; see D-032.
      fullUnitWeightLbs: 650, // BR-02
      tareLbsPerUnit: 10, // the container itself: 650 - (40 x 16) = 10 (D-032)
      unitNoun: 'tote',
      unitNounPlural: 'totes'
    })
  }),

  /**
   * D-021 — totes are billed AS ORDERED, not per full tote.
   *
   * Two DISTINCT quantities, both derived from cubic feet. Conflating them is a
   * bug the Sprint 4 test fixture is written to catch (03-business-rules.md §6):
   *
   *   raw volume     = L * W * (D/12)                -> display only
   *   billed volume  = ceil(rawVolume)                -> price AND cart quantity
   *   physical units = ceil(rawVolume / capacity)     -> freight weight, fulfilment
   *
   * Billed volume is CEILED (D-021 as amended 2026-08-12, OQ-43): the real
   * Shopify variants are priced "Per cu. ft." so cart quantity IS cubic feet,
   * and Shopify cart quantities are integers. Rounding up is consistent with
   * BR-20 and never leaves a customer short. Impact: at most 1 cu ft.
   *
   * At 108 cu ft the customer receives 3 totes and pays for 108 cu ft, not 120.
   */
  billingBasis: 'as_ordered_rounded_up', // D-021 as amended — ceil(cubicFeet)

  /* ---------------------------------------------------------------------
   * DIMENSIONS — D-018 (units), D-020 (installation type + depth defaults)
   * ------------------------------------------------------------------- */
  dimensions: Object.freeze({
    lengthUnit: 'feet', // D-018
    widthUnit: 'feet', // D-018
    diameterUnit: 'feet', // D-028
    depthUnit: 'inches', // D-018

    /**
     * D-028 — the project area can be a RECTANGLE or a CIRCLE.
     *
     * Added for feature parity with the legacy calculator at
     * /pages/rubber-mulch-calculator, which has always supported a diameter
     * input. Round trampoline bases and circular play areas are real.
     *
     *   rectangle: square_feet = length_ft x width_ft            BR-05
     *   circle:    square_feet = PI x (diameter_ft / 2)^2         BR-05a
     *
     * Everything downstream is unchanged: cubic feet, billing, rounding,
     * packaging, weight and freight all consume square_feet and never care how
     * it was derived.
     *
     * Rectangle is the default: it is the overwhelmingly common case, and
     * pre-selecting it means the form works unchanged for most customers
     * instead of opening on an empty state.
     */
    defaultShape: 'rectangle',
    shapes: Object.freeze({
      rectangle: Object.freeze({
        id: 'rectangle',
        label: 'Rectangle',
        fields: Object.freeze(['length', 'width'])
      }),
      circle: Object.freeze({
        id: 'circle',
        label: 'Circle',
        fields: Object.freeze(['diameter'])
      })
    }),

    /**
     * D-020 (corrected) — ONE recommended depth for every installation type.
     *
     * 3 inches is the Product Owner's stated recommendation for Rainbow Mulch's
     * product, which is RUBBER MULCH. An earlier version of this file carried a
     * two-tier default (9" new install / 3" top-up) derived from engineered wood
     * fiber guidance. That guidance does not apply to rubber mulch and the
     * two-tier logic has been removed.
     *
     * D-015 still governs: this is a DEFAULT, never a limit. Any depth is
     * enterable, and depth must never appear in validation logic.
     */
    depthDefault: 3
  }),

  /**
   * Validation bounds — OQ-09 STILL OPEN.
   * Working assumption only: reject zero, negative, and non-numeric.
   * No minimum or maximum order is enforced.
   */
  validation: Object.freeze({
    minDimension: 0, // exclusive — must be > 0
    maxDimension: null // no maximum enforced (OQ-09)
  }),

  /**
   * Backend routes reached through the App Proxy (D-039, D-044).
   *
   * Shares a base path with freight.quoteEndpoint. Kept as literals because
   * PBConfig is one frozen object and cannot reference itself while being built;
   * consolidating both onto a single `proxyBase` is a worthwhile tidy-up, but it
   * would be a change to freight's contract and belongs in its own commit.
   */
  backend: Object.freeze({
    specialistEndpoint: '/apps/playground-builder/specialist',
    // D-061 — Email Project's own endpoint, distinct from Save's. Save stays
    // anonymous (D-003); this one collects an email + consent, because
    // sending something TO an address is the whole point of this action.
    emailEndpoint: '/apps/playground-builder/project/email',
    saveEndpoint: '/apps/playground-builder/project',
    // P-002, Sprint 12 — GET, ?id=<token> appended by the caller. Same route
    // as saveEndpoint, different method; kept as its own config key rather
    // than derived, so a future path change to one cannot silently break the
    // other by a find-and-replace that only catches one of the two call sites.
    getProjectEndpoint: '/apps/playground-builder/project',
    // D-047 — single entry point for BOTH checkout paths. The server decides
    // which path an order takes; the client never chooses by calling a
    // different URL (that would let a tampered client force the cheap path
    // on a large order — see the security note on handleCheckout).
    checkoutEndpoint: '/apps/playground-builder/checkout'
  }),

  /**
   * P-002, Sprint 12 — where a saved project's shareable link points. A
   * config value, not a hardcoded string in pb-builder.js, for the same
   * reason every other business-shaped constant lives here (D-010): the page
   * handle is a Shopify admin fact, not a code fact, and must not require a
   * JS edit if it ever changes.
   */
  savedProject: Object.freeze({
    reopenPath: '/pages/project',

    /**
     * D-061 — the backend has no `window.location.origin` to build a reopen
     * URL from (unlike the client, which already uses that directly), so the
     * real custom domain lives here instead. One value, read by both sides —
     * D-010 applied to a domain instead of a business rule.
     */
    siteUrl: 'https://rainbowmulch.com'
  }),

  /**
   * D-047 — hybrid checkout, split by pre-tax total (material + freight).
   *
   * WHY: documented Shopify Payments fund-hold risk for large, first-of-their-
   * kind transactions (90–120 days, $4,000–$29,000, corroborated against
   * Shopify's own reserves documentation — see D-047's Sources). Rainbow
   * Mulch's real payment history is small-bag orders; this project's target
   * order size ($3,000–$5,000) sits squarely in the risk band.
   */
  checkout: Object.freeze({
    /**
     * Orders at or above this pre-tax total route to a human-reviewed Draft
     * Order invoice instead of instant checkout. EXPECTED TO MOVE UPWARD as
     * Shopify Payments transaction history builds and hold risk falls —
     * this is exactly why it lives here and not as a literal in a route.
     */
    draftOrderThreshold: 1000,

    /**
     * D-047/OQ-29 (Path 1, under-threshold). Shopify Basic has no native way
     * to carry an arbitrary-priced cart line item — no Shopify Functions on a
     * CUSTOM app, no Carrier Service API (D-011/P-003, requires Advanced+).
     * The workaround is a REAL product priced at exactly $1.00, added to cart
     * at a quantity equal to the freight amount rounded up to the next whole
     * dollar (D-021/BR-20a's "always round up" convention, applied to freight
     * for the first time). The product name is the only thing that can make
     * "Freight (billed per $1) × 263" read sensibly — Basic cannot relabel a
     * checkout line item (that customization is Plus-only).
     *
     * RESOLVES OQ-54. Product created by the Product Owner 2026-08-24:
     * "Freight (billed per $1)", product id 8210048516189, variant fetched
     * and verified against the live public product JSON (not guessed):
     *   price:  $1.00 exactly
     *   handle: freight-billed-per-1
     * Also verified excluded from everywhere a customer could stumble onto
     * it — the point of this product existing at all is that it should never
     * be seen except as a checkout line item:
     *   - sitemap_products_1.xml (the shard whose own range ends at this
     *     product's id) does NOT list it
     *   - on-site predictive search for "freight"/"billed" returns zero
     *     results (control query for a real product confirmed the endpoint
     *     itself works)
     *   - /collections/all AND all 5 named collections do not contain it
     *
     * ⚠️ CONFIGURED CORRECTLY, BUT NOT YET PURCHASABLE — see OQ-55. A real
     * POST to /cart/add.js with this exact variant returned 422 "already sold
     * out". Same result for EVERY sampled variant of non-toxic-playground-
     * surface (both packagings, multiple colours, Mix Your Own) — a
     * pre-existing, store-wide inventory-policy block, not something this
     * product introduced. `ready: true` in checkout.js's response means only
     * "a variant id is configured" — it cannot and does not check live
     * Shopify inventory, so it is accurate to what it tests, not a claim that
     * Path 1 fully works end-to-end yet.
     */
    freightUnit: Object.freeze({
      variantId: 44940137267293,
      priceUsd: 1
    })
  }),


  /* ---------------------------------------------------------------------
   * COLOUR & BLENDS — BR-15..BR-19, BR-16a, D-014, D-016
   * ------------------------------------------------------------------- */
  color: Object.freeze({
    /**
     * BR-16 / BR-17 — only these three blends. No custom percentages.
     * "Mix Your Own" is a marketing LABEL for this system (BR-16a, D-014).
     * There must be no separate Mix Your Own branch anywhere in the code.
     */
    blends: Object.freeze([
      Object.freeze({ count: 2, label: '50/50', share: 50 }),
      Object.freeze({ count: 3, label: '33/33/33', share: 33 }),
      Object.freeze({ count: 4, label: '25/25/25/25', share: 25 })
    ]),

    /**
     * D-016 — blends are physically mixed in-tote, so quantity rounds up ONCE
     * on total volume, never per colour. Colour is therefore fully cost-neutral:
     * no effect on price, quantity, weight, or lead time.
     *
     * The quantity calculator must NOT receive the colour list at all.
     */
    affectsQuantity: false,
    affectsPrice: false,
    affectsLeadTime: false,

    /**
     * REAL CATALOGUE with REAL SWATCH IMAGES.
     *
     * Colours pulled from the live product `non-toxic-playground-surface`
     * (32 variants = Packaging x Color). Swatch images are Rainbow Mulch's own
     * purpose-made swatches from /pages/colored-rubber-mulch, each explicitly
     * labelled by colour name — no inference or guessing involved (D-034).
     *
     * All 11 URLs verified live: HTTP 206, content-type image/png.
     *
     * D-026 — the configurator offers these 11 SOLID colours plus "Mix Your Own".
     * The 4 curated preset blends that also exist as Color values in Shopify
     * (Meadow, Party, Americana, Tropical) are DELIBERATELY EXCLUDED — see OQ-42.
     *
     * "Mix Your Own" is NOT listed here. It is not a colour; it is the blend
     * mechanism (BR-16, BR-16a, D-014), and on the live store it is a real
     * product at /products/confetti-mix-mix-your-own. Never add it to this array
     * or it will render as a selectable swatch.
     *
     * WHY CDN URLS RATHER THAN THEME ASSETS: these already live on the store's
     * own Shopify CDN. Copying them into assets/ would duplicate them, add ~11
     * files to every theme push, and let the two copies drift apart when a
     * merchant updates a swatch. Referencing the CDN keeps one source of truth.
     */
    catalogIsPlaceholder: false,
    catalog: Object.freeze([
      Object.freeze({
        id: 'sandstone', name: 'Sandstone', swatch: 'https://cdn.shopify.com/s/files/1/0091/7366/3849/files/sandstone_png.png?v=1772647900',
        /**
         * D-046 — confetti-piece scatter renderer (replaces D-035's tiled bands).
         * 4 real photographed cutouts per colour, ported from the legacy
         * build-your-confetti-mix page. Stored WITHOUT ?width=, so the render
         * module owns the size/format decision, not config (D-010: config holds
         * data, not a rendering choice).
         */
        confettiPieces: Object.freeze(['https://rainbowmulch.com/cdn/shop/files/sandstone-piece-1.png?v=6217592650934552031', 'https://rainbowmulch.com/cdn/shop/files/sandstone-piece-2.png?v=1894019345308610569', 'https://rainbowmulch.com/cdn/shop/files/sandstone-piece-3.png?v=1850139173072156447', 'https://rainbowmulch.com/cdn/shop/files/sandstone-piece-4.png?v=7686361859303061153']),
      /**
       * D-047 — real Shopify variant ids on `non-toxic-playground-surface`,
       * fetched from the live product JSON and verified 1:1 against this
       * catalog (all 11 colours matched, no gaps). Lets the backend add the
       * CORRECT priced variant to cart/Draft Order rather than trusting a
       * client-supplied id (Sprint 10 cart-mapping note, OQ-14).
       */
      shopifyVariants: Object.freeze({ bags: 42326469476445, tote: 42326469509213 })
      }),
      Object.freeze({
        id: 'driftwood', name: 'Driftwood', swatch: 'https://cdn.shopify.com/s/files/1/0091/7366/3849/files/driftwood_png.png?v=1772647904',
        /**
         * D-046 — confetti-piece scatter renderer (replaces D-035's tiled bands).
         * 4 real photographed cutouts per colour, ported from the legacy
         * build-your-confetti-mix page. Stored WITHOUT ?width=, so the render
         * module owns the size/format decision, not config (D-010: config holds
         * data, not a rendering choice).
         */
        confettiPieces: Object.freeze(['https://rainbowmulch.com/cdn/shop/files/driftwood-piece-1.png?v=10363016674053731151', 'https://rainbowmulch.com/cdn/shop/files/driftwood-piece-2.png?v=14216339657934488424', 'https://rainbowmulch.com/cdn/shop/files/driftwood-piece-3.png?v=2988186064834930122', 'https://rainbowmulch.com/cdn/shop/files/driftwood-piece-4.png?v=8043002660899011908']),
      /**
       * D-047 — real Shopify variant ids on `non-toxic-playground-surface`,
       * fetched from the live product JSON and verified 1:1 against this
       * catalog (all 11 colours matched, no gaps). Lets the backend add the
       * CORRECT priced variant to cart/Draft Order rather than trusting a
       * client-supplied id (Sprint 10 cart-mapping note, OQ-14).
       */
      shopifyVariants: Object.freeze({ bags: 42334848614493, tote: 42334849106013 })
      }),
      Object.freeze({
        id: 'natural-brown', name: 'Natural Brown', swatch: 'https://cdn.shopify.com/s/files/1/0091/7366/3849/files/natural-brown_png_5553910b-3504-42ac-a799-37d343984cbf.png?v=1772648269',
        /**
         * D-046 — confetti-piece scatter renderer (replaces D-035's tiled bands).
         * 4 real photographed cutouts per colour, ported from the legacy
         * build-your-confetti-mix page. Stored WITHOUT ?width=, so the render
         * module owns the size/format decision, not config (D-010: config holds
         * data, not a rendering choice).
         */
        confettiPieces: Object.freeze(['https://rainbowmulch.com/cdn/shop/files/natural-brown-piece-1.png?v=6497803641836749624', 'https://rainbowmulch.com/cdn/shop/files/natural-brown-piece-2.png?v=17023764300153203523', 'https://rainbowmulch.com/cdn/shop/files/natural-brown-piece-3.png?v=4805102423327777332', 'https://rainbowmulch.com/cdn/shop/files/natural-brown-piece-4.png?v=4122104487931717364']),
      /**
       * D-047 — real Shopify variant ids on `non-toxic-playground-surface`,
       * fetched from the live product JSON and verified 1:1 against this
       * catalog (all 11 colours matched, no gaps). Lets the backend add the
       * CORRECT priced variant to cart/Draft Order rather than trusting a
       * client-supplied id (Sprint 10 cart-mapping note, OQ-14).
       */
      shopifyVariants: Object.freeze({ bags: 42334848647261, tote: 42334849138781 })
      }),
      Object.freeze({
        id: 'grassy-green', name: 'Grassy Green', swatch: 'https://cdn.shopify.com/s/files/1/0091/7366/3849/files/grassy-green_png.png?v=1772647905',
        /**
         * D-046 — confetti-piece scatter renderer (replaces D-035's tiled bands).
         * 4 real photographed cutouts per colour, ported from the legacy
         * build-your-confetti-mix page. Stored WITHOUT ?width=, so the render
         * module owns the size/format decision, not config (D-010: config holds
         * data, not a rendering choice).
         */
        confettiPieces: Object.freeze(['https://rainbowmulch.com/cdn/shop/files/grassy-green-piece-1.png?v=954752180062776696', 'https://rainbowmulch.com/cdn/shop/files/grassy-green-piece-2.png?v=12852028445168919759', 'https://rainbowmulch.com/cdn/shop/files/grassy-green-piece-3.png?v=13221426307143978995', 'https://rainbowmulch.com/cdn/shop/files/grassy-green-piece-4.png?v=12913385127230943774']),
      /**
       * D-047 — real Shopify variant ids on `non-toxic-playground-surface`,
       * fetched from the live product JSON and verified 1:1 against this
       * catalog (all 11 colours matched, no gaps). Lets the backend add the
       * CORRECT priced variant to cart/Draft Order rather than trusting a
       * client-supplied id (Sprint 10 cart-mapping note, OQ-14).
       */
      shopifyVariants: Object.freeze({ bags: 42334848680029, tote: 42334849171549 })
      }),
      Object.freeze({
        id: 'harbor-blue', name: 'Harbor Blue', swatch: 'https://cdn.shopify.com/s/files/1/0091/7366/3849/files/harbor-blue_png.png?v=1772647908',
        /**
         * D-046 — confetti-piece scatter renderer (replaces D-035's tiled bands).
         * 4 real photographed cutouts per colour, ported from the legacy
         * build-your-confetti-mix page. Stored WITHOUT ?width=, so the render
         * module owns the size/format decision, not config (D-010: config holds
         * data, not a rendering choice).
         */
        confettiPieces: Object.freeze(['https://rainbowmulch.com/cdn/shop/files/harbor-blue-piece-1.png?v=11256902630628897634', 'https://rainbowmulch.com/cdn/shop/files/harbor-blue-piece-2.png?v=12727252540334005316', 'https://rainbowmulch.com/cdn/shop/files/harbor-blue-piece-3.png?v=7710971006782730364', 'https://rainbowmulch.com/cdn/shop/files/harbor-blue-piece-4.png?v=6394017246374898899']),
      /**
       * D-047 — real Shopify variant ids on `non-toxic-playground-surface`,
       * fetched from the live product JSON and verified 1:1 against this
       * catalog (all 11 colours matched, no gaps). Lets the backend add the
       * CORRECT priced variant to cart/Draft Order rather than trusting a
       * client-supplied id (Sprint 10 cart-mapping note, OQ-14).
       */
      shopifyVariants: Object.freeze({ bags: 42334848712797, tote: 42334849204317 })
      }),
      Object.freeze({
        id: 'pacific-teal', name: 'Pacific Teal', swatch: 'https://cdn.shopify.com/s/files/1/0091/7366/3849/files/pacific-teal_png.png?v=1772647911',
        /**
         * D-046 — confetti-piece scatter renderer (replaces D-035's tiled bands).
         * 4 real photographed cutouts per colour, ported from the legacy
         * build-your-confetti-mix page. Stored WITHOUT ?width=, so the render
         * module owns the size/format decision, not config (D-010: config holds
         * data, not a rendering choice).
         */
        confettiPieces: Object.freeze(['https://rainbowmulch.com/cdn/shop/files/pacific-teal-piece-1.png?v=3237965526439522338', 'https://rainbowmulch.com/cdn/shop/files/pacific-teal-piece-2.png?v=17763111380790905795', 'https://rainbowmulch.com/cdn/shop/files/pacific-teal-piece-3.png?v=17671344093423670546', 'https://rainbowmulch.com/cdn/shop/files/pacific-teal-piece-4.png?v=18348579984958724481']),
      /**
       * D-047 — real Shopify variant ids on `non-toxic-playground-surface`,
       * fetched from the live product JSON and verified 1:1 against this
       * catalog (all 11 colours matched, no gaps). Lets the backend add the
       * CORRECT priced variant to cart/Draft Order rather than trusting a
       * client-supplied id (Sprint 10 cart-mapping note, OQ-14).
       */
      shopifyVariants: Object.freeze({ bags: 42334848745565, tote: 42334849237085 })
      }),
      Object.freeze({
        id: 'deep-seaglass', name: 'Deep Seaglass', swatch: 'https://cdn.shopify.com/s/files/1/0091/7366/3849/files/deep-seaglass_png.png?v=1772647908',
        /**
         * D-046 — confetti-piece scatter renderer (replaces D-035's tiled bands).
         * 4 real photographed cutouts per colour, ported from the legacy
         * build-your-confetti-mix page. Stored WITHOUT ?width=, so the render
         * module owns the size/format decision, not config (D-010: config holds
         * data, not a rendering choice).
         */
        confettiPieces: Object.freeze(['https://rainbowmulch.com/cdn/shop/files/deep-seaglass-piece-1.png?v=6361000256162914923', 'https://rainbowmulch.com/cdn/shop/files/deep-seaglass-piece-2.png?v=9057832378240682719', 'https://rainbowmulch.com/cdn/shop/files/deep-seaglass-piece-3.png?v=1354159678670528743', 'https://rainbowmulch.com/cdn/shop/files/deep-seaglass-piece-4.png?v=12898503324626247146']),
      /**
       * D-047 — real Shopify variant ids on `non-toxic-playground-surface`,
       * fetched from the live product JSON and verified 1:1 against this
       * catalog (all 11 colours matched, no gaps). Lets the backend add the
       * CORRECT priced variant to cart/Draft Order rather than trusting a
       * client-supplied id (Sprint 10 cart-mapping note, OQ-14).
       */
      shopifyVariants: Object.freeze({ bags: 42334848778333, tote: 42334849269853 })
      }),
      Object.freeze({
        id: 'tuscany-gold', name: 'Tuscany Gold', swatch: 'https://cdn.shopify.com/s/files/1/0091/7366/3849/files/tuscany-gold_png.png?v=1772647906',
        /**
         * D-046 — confetti-piece scatter renderer (replaces D-035's tiled bands).
         * 4 real photographed cutouts per colour, ported from the legacy
         * build-your-confetti-mix page. Stored WITHOUT ?width=, so the render
         * module owns the size/format decision, not config (D-010: config holds
         * data, not a rendering choice).
         */
        confettiPieces: Object.freeze(['https://rainbowmulch.com/cdn/shop/files/tuscany-gold-piece-1.png?v=9752260691251887740', 'https://rainbowmulch.com/cdn/shop/files/tuscany-gold-piece-2.png?v=16394104701964172350', 'https://rainbowmulch.com/cdn/shop/files/tuscany-gold-piece-3.png?v=16717304085620145573', 'https://rainbowmulch.com/cdn/shop/files/tuscany-gold-piece-4.png?v=7058133620312405852']),
      /**
       * D-047 — real Shopify variant ids on `non-toxic-playground-surface`,
       * fetched from the live product JSON and verified 1:1 against this
       * catalog (all 11 colours matched, no gaps). Lets the backend add the
       * CORRECT priced variant to cart/Draft Order rather than trusting a
       * client-supplied id (Sprint 10 cart-mapping note, OQ-14).
       */
      shopifyVariants: Object.freeze({ bags: 42334848811101, tote: 42334849302621 })
      }),
      Object.freeze({
        id: 'scarlet-red', name: 'Scarlet Red', swatch: 'https://cdn.shopify.com/s/files/1/0091/7366/3849/files/scarlet-red_png.png?v=1772647907',
        /**
         * D-046 — confetti-piece scatter renderer (replaces D-035's tiled bands).
         * 4 real photographed cutouts per colour, ported from the legacy
         * build-your-confetti-mix page. Stored WITHOUT ?width=, so the render
         * module owns the size/format decision, not config (D-010: config holds
         * data, not a rendering choice).
         */
        confettiPieces: Object.freeze(['https://rainbowmulch.com/cdn/shop/files/scarlet-red-piece-1.png?v=15485351711409135191', 'https://rainbowmulch.com/cdn/shop/files/scarlet-red-piece-2.png?v=14699558531145065866', 'https://rainbowmulch.com/cdn/shop/files/scarlet-red-piece-3.png?v=4403138546601123759', 'https://rainbowmulch.com/cdn/shop/files/scarlet-red-piece-4.png?v=16669478291676816327']),
      /**
       * D-047 — real Shopify variant ids on `non-toxic-playground-surface`,
       * fetched from the live product JSON and verified 1:1 against this
       * catalog (all 11 colours matched, no gaps). Lets the backend add the
       * CORRECT priced variant to cart/Draft Order rather than trusting a
       * client-supplied id (Sprint 10 cart-mapping note, OQ-14).
       */
      shopifyVariants: Object.freeze({ bags: 42334848843869, tote: 42334849335389 })
      }),
      Object.freeze({
        id: 'black-plum', name: 'Black Plum', swatch: 'https://cdn.shopify.com/s/files/1/0091/7366/3849/files/black-plum_png.png?v=1772647903',
        /**
         * D-046 — confetti-piece scatter renderer (replaces D-035's tiled bands).
         * 4 real photographed cutouts per colour, ported from the legacy
         * build-your-confetti-mix page. Stored WITHOUT ?width=, so the render
         * module owns the size/format decision, not config (D-010: config holds
         * data, not a rendering choice).
         */
        confettiPieces: Object.freeze(['https://rainbowmulch.com/cdn/shop/files/black-plum-piece-1.png?v=8290845919856553265', 'https://rainbowmulch.com/cdn/shop/files/black-plum-piece-2.png?v=10755086294274019521', 'https://rainbowmulch.com/cdn/shop/files/black-plum-piece-3.png?v=10259700029120792960', 'https://rainbowmulch.com/cdn/shop/files/black-plum-piece-4.png?v=10728126752544967567']),
      /**
       * D-047 — real Shopify variant ids on `non-toxic-playground-surface`,
       * fetched from the live product JSON and verified 1:1 against this
       * catalog (all 11 colours matched, no gaps). Lets the backend add the
       * CORRECT priced variant to cart/Draft Order rather than trusting a
       * client-supplied id (Sprint 10 cart-mapping note, OQ-14).
       */
      shopifyVariants: Object.freeze({ bags: 42334848876637, tote: 42334849368157 })
      }),
      Object.freeze({
        id: 'cool-black', name: 'Cool Black', swatch: 'https://cdn.shopify.com/s/files/1/0091/7366/3849/files/cool-black_png.png?v=1772647891',
        /**
         * D-046 — confetti-piece scatter renderer (replaces D-035's tiled bands).
         * 4 real photographed cutouts per colour, ported from the legacy
         * build-your-confetti-mix page. Stored WITHOUT ?width=, so the render
         * module owns the size/format decision, not config (D-010: config holds
         * data, not a rendering choice).
         */
        confettiPieces: Object.freeze(['https://rainbowmulch.com/cdn/shop/files/cool-black-piece-1.png?v=1049929455056445579', 'https://rainbowmulch.com/cdn/shop/files/cool-black-piece-2.png?v=2843290015288163620', 'https://rainbowmulch.com/cdn/shop/files/cool-black-piece-3.png?v=8328212773162544456', 'https://rainbowmulch.com/cdn/shop/files/cool-black-piece-4.png?v=8134698925904856717']),
      /**
       * D-047 — real Shopify variant ids on `non-toxic-playground-surface`,
       * fetched from the live product JSON and verified 1:1 against this
       * catalog (all 11 colours matched, no gaps). Lets the backend add the
       * CORRECT priced variant to cart/Draft Order rather than trusting a
       * client-supplied id (Sprint 10 cart-mapping note, OQ-14).
       */
      shopifyVariants: Object.freeze({ bags: 42334848909405, tote: 42334849400925 })
      }),
    ]),

    /**
     * Excluded from the builder by D-026, but still live in Shopify (OQ-42).
     * Listed only so nobody re-adds them believing it an oversight.
     */
    excludedPresetBlends: Object.freeze([
      'Meadow Blend', 'Party Blend', 'Americana Blend', 'Tropical Blend'
    ]),

    /**
     * Representative image for the "Mix Your Own" MODE CARD — what a mixed order
     * actually looks like. Used on the mode selector, not in the colour grid.
     * Live product: /products/confetti-mix-mix-your-own (D-026 confirmed).
     *
     * 2026-09-14 — now a LOCAL theme asset (assets/mix-your-own-header.png),
     * not a Shopify Files CDN URL. This file (pb-config.js) is a plain static
     * asset with no Liquid processing, so it cannot resolve asset_url itself;
     * playground-builder.liquid resolves it and hands it off via
     * window.PBAssetURLs.mixYourOwnHeader before this file loads (see that
     * section for why). Falls back to '' — never a stale/wrong image — if
     * loaded somewhere that bridge hasn't run.
     */
    mixYourOwnImage: (typeof window !== 'undefined' && window.PBAssetURLs && window.PBAssetURLs.mixYourOwnHeader) || '',

    /** The real Shopify Color value that a Mix Your Own blend resolves to at cart time. */
    blendVariantColorValue: 'Mix Your Own',

    /**
     * D-047 (Sprint 10) — real variant ids for the "Mix Your Own" Color value,
     * fetched from the live product JSON. A blend of 2–4 colours resolves to
     * this ONE variant per packaging, with the chosen colours and their
     * shares carried as line-item properties (cart) / custom attributes
     * (Draft Order) — not as separate variants (OQ-14's cart-mapping note).
     */
    mixYourOwnVariants: Object.freeze({ bags: 42334849073245, tote: 42334849564765 })
  }),

  /* ---------------------------------------------------------------------
   * LEAD TIME — D-012, LT-01..LT-05
   * FLAT for all colours and all blends. This is settled, not calculated.
   * ------------------------------------------------------------------- */
  leadTime: Object.freeze({
    label: '2–3 weeks',

    /**
     * D-036 (resolving OQ-05) — this is PRODUCTION time, not arrival time.
     *
     * The qualifier lives here, in ONE place, because it is the whole meaning of
     * the figure. "2–3 weeks" alone is ambiguous and "Arrives in 2–3 weeks" would
     * be wrong: freight transit is separate and is shown beside the freight quote.
     *
     * It used to be hardcoded in the panel markup AND string-concatenated in
     * pb-builder.js — the same customer-facing phrase from two sources, which is
     * exactly the drift D-010 exists to prevent.
     */
    qualifier: 'Ships in',

    minDays: 14,
    maxDays: 21,

    /**
     * D-012 / LT-06 — per-colour lead times are DEFERRED, NOT REMOVED.
     * The single category below is what preserves that. When differentiation
     * returns, add categories here and map colours to them — a CONFIG change,
     * not a code change.
     *
     * LT-07: do NOT build UI for a distinction that is currently always identical.
     */
    categories: Object.freeze({
      standard: Object.freeze({ label: '2–3 weeks', minDays: 14, maxDays: 21 })
    }),
    defaultCategory: 'standard',

    /**
     * OQ-56 (resolved as D-060) — an OVERRIDE, not a change to the base rule
     * above. Manufacturing capacity for a playground-scale order plausibly
     * differs from a typical backyard project, so above this weight we stop
     * promising a flat window we can't back and tell the customer staff will
     * confirm a real one instead. `used by both the storefront (pb-
     * calculator.js's derive()) and the backend (shared-rules.js's
     * isLargeOrder()) — one threshold, D-010's single-source-of-truth rule
     * applied across the client/server boundary, same as everything else in
     * this file.
     */
    largeOrder: Object.freeze({
      thresholdLbs: 5000,
      label: '2–3+ weeks — we’ll confirm your exact timeline'
    })
  }),

  /**
   * D-013 — rush delivers a flat 1–2 weeks and is DEFERRED to a post-launch
   * sprint. Present here only so the shape exists; NOT built, NOT rendered.
   *
   * D-005 / LT-09: rush must NEVER be a parameter of the base lead-time
   * calculation. It is a separate paid add-on with its own timeline.
   */
  rush: Object.freeze({
    enabled: false, // deferred — do not render
    label: '1–2 weeks',
    minDays: 7,
    maxDays: 14,
    price: null // OQ-06 — unspecified, needed only at the rush sprint
  }),

  /**
   * TAX — D-002 (Shopify Tax is authoritative), D-017 (freight is taxable).
   *
   * This rate is used ONLY to show an estimate in the builder. The real figure
   * always comes from Shopify Tax at checkout, and the summary says so.
   *
   * Moved here from pb-mocks.js in Sprint 8 (D-037): an estimate rate the
   * business chooses to display is a configuration value, not a fake number,
   * and Sprint 11 needs somewhere real to put tax settings.
   *
   * ⚠️ OQ-47 OPEN — this is applied FLAT to every destination. For a customer
   * outside Ohio, where nexus is unconfirmed (OQ-30), it OVER-STATES the total.
   * On the canonical order that is ~$540 of tax a customer may not owe, which
   * works directly against objective 8. Destination-awareness is Sprint 11.
   */
  tax: Object.freeze({
    /**
     * D-038 (resolving OQ-47) — NO tax figure is shown in the builder.
     *
     * `showEstimate: false` is the switch. While it is false the builder shows
     * "Calculated at checkout" instead of a number, and the displayed total is
     * material + freight only, explicitly labelled as excluding tax.
     *
     * WHY: the rate below is applied flat to every destination, and nexus
     * outside Ohio is unconfirmed (OQ-30). Showing it would over-state the
     * canonical order by ~$540 for a customer who may owe nothing. Over-stating
     * is the direction that loses a sale outright, and it works directly against
     * objective 8 — so no number is more honest than a wrong one.
     *
     * TEMPORARY. To switch back on, this needs BOTH:
     *   1. OQ-30 answered — which states Rainbow Mulch actually collects in; and
     *   2. ZIP -> state resolution, which the builder does NOT have today. It
     *      collects a ZIP on Step 4 but cannot map it to a state, so it cannot
     *      currently tell an Ohio customer from a California one.
     * Flipping this flag alone would restore the same wrong behaviour.
     *
     * Sprint 11 makes this moot for the real charge: Shopify Tax is
     * authoritative at checkout (D-002) regardless of what the builder displays.
     */
    showEstimate: false,

    estimatedRate: 0.075, // retained for when showEstimate is re-enabled
    appliesToFreight: true, // D-017 — Ohio taxes delivery on taxable goods
    destinationAware: false, // no ZIP -> state resolution exists yet
    nexusConfirmedStates: Object.freeze(['OH']), // D-017; everywhere else is OQ-30
    placeholderLabel: 'Calculated at checkout',
    authoritativeSource: 'Shopify Tax at checkout' // D-002
  }),

  /* ---------------------------------------------------------------------
   * FREIGHT — D-001, D-004, D-011, SH-01..SH-21
   * ------------------------------------------------------------------- */
  freight: Object.freeze({
    carrier: 'R+L Carriers', // D-001
    originZip: '45159',
    freightClass: 50,

    /**
     * D-070 (2026-09-04) — R+L's Rate Quote API notified by email that its
     * per-Item weight ceiling drops from 20,000 lbs to 16,000 lbs, effective
     * Monday 2026-09-07. Requests above this return an API error, not a quote.
     *
     * SINGLE SOURCE OF TRUTH for that ceiling: `backend/src/routes/freight.js`
     * reads this same value for the pre-flight guard that stops an over-limit
     * request ever reaching R+L, and `pb-freight-fallback.js`'s estimateRange()
     * reads it to withhold the fitted fallback range above this weight too
     * (see that file's header for why — D-042's model has no real training
     * support up here). One number, so the API guard and the display range can
     * never independently drift apart on where this line sits.
     */
    maxQuotableWeightLbs: 16000,

    /**
     * D-011 — Rainbow Mulch is on Shopify Basic, so the Carrier Service API is
     * unavailable. Freight is carried as a CUSTOM LINE ITEM, calculated in the
     * backend during the configurator.
     *
     * SH-18 — the freight amount must be established SERVER-SIDE. It must never
     * originate in this file or anywhere else in the browser: a browser-set
     * price can be edited from $800 to $8.
     */
    deliveryMechanism: 'custom_line_item',
    taxable: true, // D-017 — Ohio treats delivery as part of the taxable sales price

    /**
     * The App Proxy path (D-039, D-043). Shopify forwards /apps/playground-builder/*
     * to the Worker after signing it, so this is SAME-ORIGIN — no CORS, and the
     * request carries a signature the browser cannot forge.
     *
     * Must match the Subpath prefix + Subpath configured in the Shopify custom
     * app. Changing one without the other yields a 404 through the proxy.
     */
    quoteEndpoint: '/apps/playground-builder/freight',

    timeoutMs: 8000, // SH-12 — explicit timeout; tune against real R+L latency in Sprint 9
    accessorials: Object.freeze({ residential: true, liftgate: true }), // SH-03

    /**
     * Item 1 (2026-09-11) — FLAT-FEE SMALL ORDERS.
     *
     * Orders at or below `maxCubicFeet` are forced to Bag packaging (a bag is
     * exactly 1 cu ft, D-031, so `billedCubicFeet` IS the bag count — no
     * separate lookup key needed) and never call R+L at all: no live quote,
     * no D-004 fallback range, no ZIP/destination variation. This is a real
     * physical fact, not a pricing choice — this volume of material does not
     * belong in a 40 cu ft tote, and ships UPS parcel, not LTL freight.
     *
     * `table` is an EXPLICIT lookup, deliberately not a formula: the real
     * UPS rates it is built from do not follow a smooth curve (bag 6 → 7
     * jumps $70 → $100, for instance), so fitting one would either miss real
     * steps or invent smoothness that is not there. Same "config holds the
     * real number, never a guess" discipline as `fallbackModel` below.
     *
     * getFreightQuote() (backend/src/routes/freight.js) checks this FIRST,
     * before the R+L weight-ceiling guard or any cache/network call — SH-18
     * still applies: the amount actually billed comes from CALC.
     * flatFeeForCubicFeet(), computed server-side from the same recomputed
     * shipment, never trusted from the client.
     */
    flatFee: Object.freeze({
      enabled: true,
      maxCubicFeet: 14,
      carrierLabel: 'UPS',
      table: Object.freeze({
        1: 30, 2: 45, 3: 50, 4: 55, 5: 65, 6: 70, 7: 100,
        8: 130, 9: 150, 10: 170, 11: 215, 12: 240, 13: 260, 14: 280
      })
    }),

    /**
     * D-042 (resolves OQ-15) — FALLBACK RANGE MODEL.
     *
     * Shown only when the live R+L call fails. These are EMPIRICALLY FITTED
     * values, not chosen ones, so they live in configuration and must be
     * re-fitted rather than hand-tuned. See assets/pb-freight-fallback.js for
     * the full derivation and the five documented limitations.
     *
     *   cost ≈ coefficient × weightLbs^weightExponent × miles^distanceExponent
     *
     * Fitted by ordinary least squares on log(cost) over 96 LTL orders from
     * data/historical-freight-orders.csv (114 rows; 12 UPS parcel and 6
     * unlabelled excluded). R² = 0.808.
     *
     * NOTE there is deliberately NO packaging term: adding one moves R² by
     * 0.0003 (F = 0.13, not significant). Bulk vs bagged $/lb differs only
     * because bagged orders are smaller, not because packaging costs more.
     *
     * ⚠️ This range is INFORMATIONAL. SH-18 still requires the amount actually
     * billed to come from a live server-side quote.
     */
    fallbackModel: Object.freeze({
      coefficient: 0.923749,
      weightExponent: 0.534965,
      distanceExponent: 0.394376,

      /**
       * Band multipliers around the point estimate. Derived from LEAVE-ONE-OUT
       * cross-validated residuals, so they describe out-of-sample error rather
       * than flattering the fit: [p5, p95] achieved 90% real coverage.
       * Widening these is safe; narrowing them without re-fitting is not.
       */
      lowMultiplier: 0.56,
      highMultiplier: 1.72,

      /** The yard. Coordinates for originZip 45159 (Midland, Ohio). */
      originLat: 39.36,
      originLon: -83.94,

      /** Floor on distance so same-region lanes cannot collapse toward zero. */
      minDistanceMiles: 25,

      /** Below this many supporting orders a band is reported low-confidence. */
      minSampleForConfidence: 20,

      /**
       * Fitted support by distance, for honest confidence reporting only —
       * these bands are NOT used to price. `local` is thin and flagged.
       */
      bands: Object.freeze([
        Object.freeze({ id: 'local', maxMiles: 300, sampleSize: 11 }),
        Object.freeze({ id: 'regional', maxMiles: 700, sampleSize: 37 }),
        Object.freeze({ id: 'mid', maxMiles: 1200, sampleSize: 20 }),
        Object.freeze({ id: 'far', maxMiles: Infinity, sampleSize: 28 })
      ]),

      sampleSize: 96,
      fittedOn: '2026-08-17',
      sourceData: 'data/historical-freight-orders.csv'
    })
  }),

  /* ---------------------------------------------------------------------
   * PACKAGING ACKNOWLEDGEMENT — D-022
   * Required for Bulk Tote only. Gates CONTINUE, never the packaging choice
   * itself: the customer confirms they can RECEIVE the tote (BR-08 untouched).
   * ------------------------------------------------------------------- */
  requireToteAcknowledgement: true,

  /* ---------------------------------------------------------------------
   * NAVIGATION — OQ-25 STILL OPEN
   * Working assumption: customers may move freely backward and edit any earlier
   * step. Changing dimensions or packaging invalidates a fetched freight quote.
   * Easy to constrain later — see pb-builder.js `canNavigateBackward`.
   * ------------------------------------------------------------------- */
  allowFreeBackwardEditing: true,

  /**
   * D-029 — NO overage or compaction allowance. SETTLED, not a placeholder.
   *
   * The quoted volume is the exact calculated geometry (BR-05 rectangle /
   * BR-05a circle), rounded up to the next whole cubic foot per BR-20 and D-021.
   * Nothing is added on top for settling.
   *
   * This zero is a recorded decision. Do NOT "fill it in" with an industry
   * figure. It stays here only so that IF Rainbow Mulch ever adopts an
   * allowance, there is exactly one place to set it (D-010).
   */
  overagePercent: 0
});
